import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { RmsModule } from '@rms';
import { RmsPulldownRef } from '../../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown-ref';
import { RMS_PULLDOWN_DATA } from '../../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown.tokens';
import { OrdersDataService } from '../../../../../../services/orders-data.service';
import { PaymentReceiptService } from '../../../../../../services/payment-receipt.service';
import { RmsMessageService, RmsPulldownService } from '@services';
import { ACH_PULL_PAYMENT_METHOD_ID } from '@shared/constants/payment-method';
import { PAYMENT_STATUSES } from '@shared/constants/payment-status';
import { ReceiptViewerComponent } from '../receipt-viewer/receipt-viewer.component';

interface TimelineStep {
  label: string;
  date: string | null;
}


const ACH_WORKFLOW_ACTIONS: Record<number, { fromLabel: string; buttons: { label: string; toStatusId: number; actionDescription: string }[] }> = {
  1: {
    fromLabel: 'Requested', buttons: [
      { label: 'Record Authorization', toStatusId: 2, actionDescription: 'Customer authorized ACH pull' },
      { label: 'Cancel', toStatusId: 8, actionDescription: 'ACH pull canceled' },
    ]
  },
  2: { fromLabel: 'Authorized', buttons: [{ label: 'Submit ACH Pull', toStatusId: 3, actionDescription: 'ACH pull submitted by Accounting' }] },
  3: {
    fromLabel: 'Submitted', buttons: [
      { label: 'Mark Processing', toStatusId: 4, actionDescription: 'ACH pull moved to processing' },
      { label: 'Mark Failed', toStatusId: 7, actionDescription: 'ACH pull failed' },
    ]
  },
  4: {
    fromLabel: 'Processing', buttons: [
      { label: 'Mark Settled', toStatusId: 5, actionDescription: 'ACH pull settled' },
      { label: 'Mark Failed', toStatusId: 7, actionDescription: 'ACH pull failed' },
    ]
  },
  // 5 Settled, 7 Failed, 8 Canceled: terminal, no further workflow actions.
};

@Component({
  selector: 'app-payment-details',
  imports: [RmsModule],
  templateUrl: './payment-details.component.html',
  styleUrl: './payment-details.component.scss'
})
export class PaymentDetailsComponent implements OnInit {
  Payment: any;
  CanEdit = false;
  CompanyName?: string;
  AuditHistory: any[] = [];
  LoadingHistory = false;
  Busy = false;
  Statuses = PAYMENT_STATUSES;

  constructor(
    @Inject(RMS_PULLDOWN_DATA) private data: any,
    public ref: RmsPulldownRef,
    private ordersData: OrdersDataService,
    private message: RmsMessageService,
    private cdr: ChangeDetectorRef,
    private paymentReceipt: PaymentReceiptService,
    private pulldown: RmsPulldownService
  ) {
    this.Payment = data.payment || {};
    this.CanEdit = !!data.canEdit;
    this.CompanyName = data.companyName;
  }

  ngOnInit() {
    this.loadHistory();
  }

  get isAchPull(): boolean {
    return this.Payment.PaymentMethodID === ACH_PULL_PAYMENT_METHOD_ID;
  }


  get statusDropdownOptions() {
    if (!this.isAchPull) return this.Statuses;
    const allowedNext = new Set((ACH_WORKFLOW_ACTIONS[this.Payment.StatusID]?.buttons ?? []).map(b => b.toStatusId));
    return this.Statuses.filter(s => s.OrderPaymentStatusID === this.Payment.StatusID || allowedNext.has(s.OrderPaymentStatusID));
  }

  get timelineSteps(): TimelineStep[] {
    const p = this.Payment;
    return [
      { label: 'Requested', date: p.RequestedDate },
      { label: 'Authorized', date: p.CustomerAuthorizedDate },
      { label: 'Submitted', date: p.SubmittedDate },
      { label: 'Processing', date: p.ProcessingDate },
      { label: 'Settled', date: p.SettledDate },
      { label: 'Confirmed', date: p.ConfirmationSentDate ?? p.SettledDate },
      { label: 'Failed', date: p.FailedDate },
      { label: 'Canceled', date: p.CanceledDate },
    ];
  }


  onStatusChange(newStatusId: number) {
    if (newStatusId === this.Payment.StatusID) return;
    const achStep = ACH_WORKFLOW_ACTIONS[this.Payment.StatusID]?.buttons.find(b => b.toStatusId === newStatusId);
    const newStatusName = PAYMENT_STATUSES.find(s => s.OrderPaymentStatusID === newStatusId)?.StatusName ?? '';
    this.advanceStatus(newStatusId, achStep?.actionDescription ?? `Status changed to ${newStatusName}`);
  }

  advanceStatus(toStatusId: number, actionDescription: string) {
    if (this.Busy || !this.CanEdit) return;
    this.Busy = true;
    const payload = { ...this.Payment, StatusID: toStatusId, ActionDescription: actionDescription };
    this.ordersData.saveOrderPayment(payload).subscribe({
      next: () => {
        this.message.success(actionDescription);
        this.refreshPayment();
      },
      error: (err) => {
        this.Busy = false;
        this.message.error('Failed to update ACH status: ' + this.errorText(err));
        this.cdr.markForCheck();
      }
    });
  }

  private refreshPayment() {

    const orderTotal = Number(this.Payment.OrderTotal) || 0;
    const wasFullyPaid = orderTotal > 0 && (Number(this.Payment.OrderBalanceDue) || 0) <= 0;
    const priorStatusId = this.Payment.StatusID;
    this.ordersData.getOrderPayments(this.Payment.OrderID).subscribe({
      next: (payments) => {
        const updated = (payments || []).find((p: any) => p.OrderPaymentID === this.Payment.OrderPaymentID);
        const paidNow = (payments || [])
          .filter((p: any) => p.StatusID === 5 || p.StatusID === 6)
          .reduce((sum: number, p: any) => sum + (Number(p.Amount) || 0), 0);

        if (updated) {
          this.Payment = {
            ...this.Payment,
            ...updated,
            OrderPaid: orderTotal > 0 ? paidNow : this.Payment.OrderPaid,
            OrderBalanceDue: orderTotal > 0 ? Math.max(0, orderTotal - paidNow) : this.Payment.OrderBalanceDue
          };
        }
        this.Busy = false;
        this.loadHistory();
        this.cdr.markForCheck();
        if (orderTotal > 0 && !wasFullyPaid && paidNow >= orderTotal) {
          this.message.success(`Order #${this.Payment.OrderID} is now fully paid - eligible for release.`);
        }
        if (updated) {
          this.paymentReceipt.generateIfNewlySettled(priorStatusId, this.Payment, this.CompanyName)
            .then(receiptBlobPath => {
              if (!receiptBlobPath) return;
              this.Payment = { ...this.Payment, ReceiptBlobPath: receiptBlobPath, ReceiptGeneratedDate: new Date().toISOString() };
              this.cdr.markForCheck();
            })
            .catch(err => console.error('Failed to generate payment receipt', err));
        }
      },
      error: () => { this.Busy = false; this.cdr.markForCheck(); }
    });
  }


  // Opens inline in an RMS pulldown (rms-file-preview fetches its own signed URL from
  // just the blob path) instead of a new browser tab - stays inside the app's own UI.
  viewReceipt() {
    if (!this.Payment.ReceiptBlobPath) return;
    this.pulldown.open(ReceiptViewerComponent, {
      title: `Receipt - Payment #${this.Payment.OrderPaymentID}`,
      width: '70vw',
      maxWidth: '900px',
      data: { path: this.Payment.ReceiptBlobPath }
    });
  }

  private loadHistory() {
    if (!this.Payment.OrderID) return;
    this.LoadingHistory = true;
    this.ordersData.getOrderHistory(this.Payment.OrderID).subscribe({
      next: (history) => {
        this.AuditHistory = history || [];
        this.LoadingHistory = false;
        this.cdr.markForCheck();
      },
      error: () => { this.LoadingHistory = false; this.cdr.markForCheck(); }
    });
  }

  private errorText(err: any): string {
    return err?.error?.error?.message || err?.message || 'Unknown error';
  }

  close() {
    this.ref.close();
  }
}
